import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMetaPixel } from '../../contexts/MetaPixelContext';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import {
    FaUser,
    FaEnvelope,
    FaPhone,
    FaComment,
    FaPaperPlane,
    FaGraduationCap,
    FaBookOpen,
    FaUsers,
    FaCheckCircle
} from 'react-icons/fa';
import Navbar from '../../components/Navbar';
import Footer from '../../components/footer';
import Scrollbar from '../../components/scrollbar';
import Logo from '../../images/logo.svg';
import './ContactPage.css';

const ContactPage = () => {
    const navigate = useNavigate();
    const { trackLead } = useMetaPixel();
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        userType: '',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Basic validation
        if (!formData.firstName || !formData.lastName || !formData.email || !formData.message) {
            toast.error('Please fill in all required fields');
            return;
        }

        if (!formData.userType) {
            toast.error('Please select your role');
            return;
        }

        setIsSubmitting(true);

        try {
            const apiBase = process.env.NODE_ENV === 'production' ? '/api' : (process.env.REACT_APP_API_URL || 'http://localhost:8000');
            const response = await fetch(`${apiBase}/api/v1/contact`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });

            if (response.ok) {
                // Meta Pixel: Track Lead for ads attribution
                trackLead({ contentName: 'contact_form', email: formData.email });
                setIsSubmitted(true);
                toast.success('Thank you for your message! We will get back to you soon.');
                setFormData({
                    firstName: '',
                    lastName: '',
                    email: '',
                    phone: '',
                    userType: '',
                    message: ''
                });
            } else {
                throw new Error('Failed to send message');
            }
        } catch (error) {
            console.error('Error sending contact form:', error);
            toast.error('Sorry, there was an error sending your message. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleUserTypeChange = (userType) => {
        setFormData(prev => ({
            ...prev,
            userType
        }));
    };

    const userTypes = [
        { value: 'parent', label: 'Parent', icon: <FaUsers /> },
        { value: 'school', label: 'School', icon: <FaGraduationCap /> },
        { value: 'teacher', label: 'Teacher', icon: <FaBookOpen /> },
        { value: 'other', label: 'Other', icon: <FaUser /> }
    ];

    if (isSubmitted) {
        return (
            <div className="contact-page">
                <Navbar Logo={Logo} hclass={'wpo-header-style-1'} />

                <div className="contact-container">
                    <div className="success-container">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="success-card"
                        >
                            <div className="success-icon">
                                <FaCheckCircle />
                            </div>
                            <h2>Message Sent Successfully!</h2>
                            <p>Thank you for reaching out to us. Our team will get back to you within 24 hours.</p>
                            <button
                                className="back-to-contact"
                                onClick={() => setIsSubmitted(false)}
                            >
                                Send Another Message
                            </button>
                        </motion.div>
                    </div>
                </div>

                <Footer />
                <Scrollbar />
            </div>
        );
    }

    return (
        <div className="contact-page">
            <Navbar Logo={Logo} hclass={'wpo-header-style-1'} />

            <div className="contact-container">
                <div className="contact-wrapper">
                    {/* Main Content - Only the form now */}
                    <div className="contact-main-content">
                        <div className="contact-form-section">
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5 }}
                                className="form-container"
                            >
                                <div className="form-header">
                                    <h2>Send us a Message</h2>
                                    <p>We'll get back to you within 24 hours</p>
                                </div>

                                <form onSubmit={handleSubmit} className="contact-form">
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label htmlFor="firstName">
                                                First Name <span className="required">*</span>
                                            </label>
                                            <div className="input-wrapper">
                                                <FaUser className="input-icon" />
                                                <input
                                                    type="text"
                                                    id="firstName"
                                                    name="firstName"
                                                    value={formData.firstName}
                                                    onChange={handleInputChange}
                                                    placeholder="Enter your first name"
                                                    required
                                                />
                                            </div>
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="lastName">
                                                Last Name <span className="required">*</span>
                                            </label>
                                            <div className="input-wrapper">
                                                <FaUser className="input-icon" />
                                                <input
                                                    type="text"
                                                    id="lastName"
                                                    name="lastName"
                                                    value={formData.lastName}
                                                    onChange={handleInputChange}
                                                    placeholder="Enter your last name"
                                                    required
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label htmlFor="email">
                                                Email Address <span className="required">*</span>
                                            </label>
                                            <div className="input-wrapper">
                                                <FaEnvelope className="input-icon" />
                                                <input
                                                    type="email"
                                                    id="email"
                                                    name="email"
                                                    value={formData.email}
                                                    onChange={handleInputChange}
                                                    placeholder="Enter your email address"
                                                    required
                                                />
                                            </div>
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="phone">
                                                Phone Number
                                            </label>
                                            <div className="input-wrapper">
                                                <FaPhone className="input-icon" />
                                                <input
                                                    type="tel"
                                                    id="phone"
                                                    name="phone"
                                                    value={formData.phone}
                                                    onChange={handleInputChange}
                                                    placeholder="Enter your phone number"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label>
                                            You are <span className="required">*</span>
                                        </label>
                                        <div className="radio-group">
                                            {userTypes.map((type) => (
                                                <button
                                                    key={type.value}
                                                    type="button"
                                                    className={`radio-option ${formData.userType === type.value ? 'selected' : ''}`}
                                                    onClick={() => handleUserTypeChange(type.value)}
                                                >
                                                    <div className="radio-content">
                                                        {type.icon}
                                                        <span>{type.label}</span>
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="message">
                                            Your Message <span className="required">*</span>
                                        </label>
                                        <div className="input-wrapper">
                                            <FaComment className="input-icon textarea-icon" />
                                            <textarea
                                                id="message"
                                                name="message"
                                                value={formData.message}
                                                onChange={handleInputChange}
                                                placeholder="How can we help you?"
                                                rows="6"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <button
                                        type="submit"
                                        className="submit-btn"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? (
                                            <div className="spinner"></div>
                                        ) : (
                                            <>
                                                <FaPaperPlane />
                                                Send Message
                                            </>
                                        )}
                                    </button>
                                </form>
                            </motion.div>
                        </div>
                    </div>
                </div>
            </div>

            <Footer />
            <Scrollbar />
        </div>
    );
};

export default ContactPage;